import { useState, useCallback, useEffect } from 'react';
import { apiFetch } from '@/lib/api';

export type TimeRange = 'today' | '24h' | '7d' | '30d' | '90d' | 'all';

export type Granularity = 'hour' | 'day';

export interface DashboardSummary {
  totalRuns: number;
  completed: number;
  errors: number;
  successRate: number; // 0..1
  activeUsers: number;
  medianRunMs: number | null; // started → completed, completed runs only
  medianQueueMs: number | null; // created → started
}

export interface TimeseriesPoint {
  bucket: string; // YYYY-MM-DD (day) or YYYY-MM-DDTHH:00 (hour), in the viewer's time zone
  runs: number;
  completed: number;
  errors: number;
}

export interface WorkflowStat {
  workflowId: string | null;
  workflowName: string | null;
  runs: number;
  completed: number;
  errors: number;
  successRate: number;
  medianRunMs: number | null;
  medianQueueMs: number | null;
}

export interface StatusStat {
  status: string;
  count: number;
}

export interface UserStat {
  uid: string | null;
  name: string | null;
  runs: number;
}

export interface ListMeta {
  shown: number;
  total: number;
  limit: number;
}

export interface DashboardStats {
  timeRange: TimeRange;
  workflowId: string | null;
  workflowSearch: string | null;
  from: string | null;
  to: string | null;
  generatedAt: string;
  timeZone: string;
  summary: DashboardSummary;
  granularity: Granularity;
  timeseries: TimeseriesPoint[];
  workflows: WorkflowStat[];
  workflowsMeta: ListMeta;
  statusBreakdown: StatusStat[];
  topUsers: UserStat[];
  topUsersMeta: ListMeta;
}

// A custom [from, to] window as YYYY-MM-DD strings. Either bound may be empty.
export interface CustomRange {
  from: string;
  to: string;
}

interface UseAnalyticsReturn {
  stats: DashboardStats | null;
  loading: boolean;
  error: string | null;
  range: TimeRange;
  setRange: (r: TimeRange) => void;
  customRange: CustomRange | null;
  setCustomRange: (r: CustomRange | null) => void;
  workflowId: string | null;
  setWorkflowId: (id: string | null) => void;
  workflowSearch: string;
  setWorkflowSearch: (q: string) => void;
  refetch: () => Promise<void>;
}

export const useAnalytics = (initialRange: TimeRange = '24h'): UseAnalyticsReturn => {
  const [stats, setStats] = useState<DashboardStats | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [range, setRangeState] = useState<TimeRange>(initialRange);
  const [customRange, setCustomRangeState] = useState<CustomRange | null>(null);
  const [workflowId, setWorkflowId] = useState<string | null>(null);
  const [workflowSearch, setWorkflowSearch] = useState('');
  // Debounce the search box so each keystroke doesn't fire a BigQuery request.
  const [debouncedSearch, setDebouncedSearch] = useState('');

  useEffect(() => {
    const t = setTimeout(() => setDebouncedSearch(workflowSearch.trim()), 350);
    return () => clearTimeout(t);
  }, [workflowSearch]);

  // Named range and custom range are mutually exclusive: picking one clears the
  // other so the active filter is always unambiguous.
  const setRange = useCallback((r: TimeRange) => {
    setCustomRangeState(null);
    setRangeState(r);
  }, []);

  const setCustomRange = useCallback((r: CustomRange | null) => {
    setCustomRangeState(r);
  }, []);

  const fetchStats = useCallback(async (): Promise<void> => {
    setLoading(true);
    setError(null);
    try {
      // tz makes the backend bucket days/hours in the viewer's local time.
      const params = new URLSearchParams({ range, tz: Intl.DateTimeFormat().resolvedOptions().timeZone });
      // A custom window (from/to) overrides the named range on the backend. Send
      // `to` as end-of-day so the selected end date is inclusive.
      if (customRange?.from || customRange?.to) {
        if (customRange.from) params.set('from', new Date(`${customRange.from}T00:00:00`).toISOString());
        if (customRange.to) params.set('to', new Date(`${customRange.to}T23:59:59.999`).toISOString());
      } else if (range === 'today') {
        // "Today" = since local midnight, recomputed on every fetch so a page
        // left open overnight rolls over on the next refresh.
        const midnight = new Date();
        midnight.setHours(0, 0, 0, 0);
        params.set('from', midnight.toISOString());
      }
      if (workflowId) params.set('workflowId', workflowId);
      if (debouncedSearch) params.set('workflowSearch', debouncedSearch);
      const res = await apiFetch(`/analytics/dashboard?${params.toString()}`);
      if (!res.ok) {
        const data = await res.json().catch(() => ({}));
        throw new Error((data as { error?: string }).error || `Failed to load analytics: ${res.status}`);
      }
      setStats(await res.json());
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Unknown error');
    } finally {
      setLoading(false);
    }
  }, [range, customRange, workflowId, debouncedSearch]);

  useEffect(() => {
    fetchStats();
  }, [fetchStats]);

  return {
    stats,
    loading,
    error,
    range,
    setRange,
    customRange,
    setCustomRange,
    workflowId,
    setWorkflowId,
    workflowSearch,
    setWorkflowSearch,
    refetch: fetchStats,
  };
};
